import type { GrowthCeilingKept } from '@messenger/utils'
import { EventCategory, logger } from 'utils/logger'

export type TextBoxLoadLogEntry = {
  itemId: string
  kind: 'tinymce' | 'lotus'
  /** The surface that painted it: `editor`, `preview` or `pdf`. */
  surface: string
  /** The frame height the row holds. */
  storedHeight: number
  /** The frame height the surface painted: the row's, on every surface. */
  paintedHeight: number
  /** The room the page leaves under the box, for the record; null when the surface could not say. */
  ceiling: number | null
  boundedBy: string | null
  /** The text was made smaller to fit the frame. */
  fitted: boolean
  /** The text does not fit even at the smallest size and overflows the frame. */
  overflowing: boolean
  /** The height the text painted at when it overflows, in px. */
  textHeight?: number
  /**
   * Set when the stored frame is past its room (`boundedBy` `preexisting`):
   * the room, what bounds it, and one line of the box's text — with
   * `storedHeight`, every number a refit into the room would take.
   */
  kept?: GrowthCeilingKept | null
  /** The scale a Lotus box's fit found; absent for a TinyMCE box. */
  scale?: number | null
  /** The Lotus box's text sizing mode, `shrink` or `fill`. */
  sizing?: string | null
  /** The maximum size in pt a Fill text box may paint; null for no limit. */
  maxFontSizePt?: number | null
}

/** A Lotus fit's scale that is not 1: the text was shrunk or filled. */
const scaledOf = (entry: TextBoxLoadLogEntry): boolean =>
  typeof entry.scale === 'number' &&
  Number.isFinite(entry.scale) &&
  entry.scale !== 1

/**
 * The one log line a load result leaves, on every surface, so a developer
 * can see what the fit did to a box without persisting any of it: nothing
 * when the text stands in the row's frame at its authored size (a Lotus fit
 * at scale 1) and the frame is within its room; otherwise one info line, to
 * Datadog and never to Sentry, when the text was made smaller to fit, when
 * the stored frame is past its room, when the text overflows at the floor —
 * the last resort — or, for a Lotus box, when the scale is anything other
 * than 1, carrying the scale, the mode and the maximum (docs/lotus/sizing.md,
 * "Load"). Every case is info: a load changes no stored frame, so no single
 * load is a warning anyone acts on. An overflow also warns in the console.
 */
export const logTextBoxLoadResult = (entry: TextBoxLoadLogEntry): void => {
  const kept = entry.kept ?? null
  if (!entry.fitted && !entry.overflowing && kept === null && !scaledOf(entry))
    return
  logger.info({
    message: entry.overflowing
      ? 'text_box_load_overflowing'
      : kept !== null
        ? 'text_box_load_preexisting'
        : 'text_box_load_fitted',
    category: EventCategory.DISCREPANCY,
    data: {
      itemId: entry.itemId,
      kind: entry.kind,
      surface: entry.surface,
      storedHeight: entry.storedHeight,
      paintedHeight: entry.paintedHeight,
      ceiling: entry.ceiling,
      boundedBy: entry.boundedBy,
      textHeight: entry.textHeight ?? null,
      keptCeiling: kept?.ceiling ?? null,
      keptBy: kept?.boundedBy ?? null,
      lineHeight: kept?.lineHeight ?? null,
      scale: entry.scale ?? null,
      sizing: entry.sizing ?? null,
      maxFontSizePt: entry.maxFontSizePt ?? null,
      persisted: false,
    },
  })
  // The logger is silent in development; the last resort is said aloud
  // there too, so a developer at a local stack sees the box it could not fit.
  if (entry.overflowing)
    console.warn(
      `text box ${entry.itemId.slice(0, 8)} (${entry.kind}, ${entry.surface}) overflows its frame at the smallest size: stored ${entry.storedHeight}px, frame ${entry.paintedHeight}px, text ${entry.textHeight ?? '?'}px, room under it ${entry.ceiling ?? 'none'} (${entry.boundedBy ?? 'unbounded'})`,
    )
}
